import type { FC } from 'react';
import React, { useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import FeedbackIcon from '@atlaskit/icon/core/migration/feedback';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import Button from '@atlaskit/button';
import EditorCloseIcon from '@atlaskit/icon/core/migration/close--editor-close';
import { N500 } from '@atlaskit/theme/colors';
import { layers } from '@atlaskit/theme/constants';

import { useSpaceId } from '@confluence/space-utils';

import { LEARN_MORE_LINK } from './constants';

export const GUEST_BANNER_TEST_ID = 'guest-banner-space-permissions-container';
export const ADD_GUEST_LINK_SUBJECT_ID = 'inviteGuest';
export const LEARN_MORE_SUBJECT_ID = 'learnMoreLink';
export const DISMISS_SUBJECT_ID = 'dismissed';

const i18n = defineMessages({
	bannerTitle: {
		id: 'external-collab-ui.guests-info-banner-space-perms.title',
		defaultMessage: 'Collaborate externally with guests',
		description: 'Title at the top of the Space Permissions page to inform users about guests',
	},
	bannerDescription: {
		id: 'external-collab-ui.guests-info-banner-space-perms.description',
		defaultMessage:
			'Guests are contractors, consultants, and other collaborators who are external to your organization. As guests, they’re limited to only one space, are clearly marked as guests, and have limited access to user information.',
		description:
			'Description at the top of the Space Permissions page to inform users about guests',
	},
	guestsSectionCTA: {
		id: 'external-collab-ui.guests-info-banner-space-perms.guests-section-cta',
		defaultMessage: 'Add a guest to this space',
		description:
			'CTA text to link user to the Guests Users section of this Space Permissions page to add and edit guest permissions',
	},
	guestsDocCTA: {
		id: 'external-collab-ui.guests-info-banner-space-perms.guests-doc-cta',
		defaultMessage: 'Learn more',
		description:
			'CTA text to link user to an external document about what guests are and how they work',
	},
	closeButtonLabel: {
		id: 'external-collab-ui.guests-info-banner-space-perms.close-banner',
		defaultMessage: 'Close',
		description: 'Accessible label to close or dismiss banner',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionMessageWrapper = styled.div({
	marginBottom: token('space.200', '16px'),
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CloseButtonWrapper = styled.div({
	position: 'absolute',
	right: token('space.100', '8px'),
	top: token('space.100', '8px'),
	zIndex: layers.card(),
});

export type GuestBannerSpacePermissionsProps = {
	onDismiss: () => void;
};

export const GuestBannerSpacePermissions: FC<GuestBannerSpacePermissionsProps> = ({
	onDismiss,
}) => {
	const [show, setShow] = useState<boolean>(true);

	const urlParams = new URLSearchParams(window.location.search);
	const spaceKey = urlParams.get('key');
	const spaceId = useSpaceId(spaceKey || '');

	const analyticsSource = {
		containerType: 'space',
		containerId: spaceId || '',
		source: 'permissionsScreen',
	};

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const onGuestsSectionLinkClick = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				...analyticsSource,
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: ADD_GUEST_LINK_SUBJECT_ID,
			},
		}).fire();
		const guestSection = document.querySelector('#external-collaborators-section');
		guestSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	const onGuestDocLinkClick = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				...analyticsSource,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: LEARN_MORE_SUBJECT_ID,
			},
		}).fire();
		window.open(LEARN_MORE_LINK, '_blank');
	};

	const onClose = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				...analyticsSource,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: DISMISS_SUBJECT_ID,
			},
		}).fire();
		// prop passed from parent component to hide feature next time
		onDismiss?.();
		setShow(false);
	};

	if (!show) {
		return null;
	}

	return (
		<SectionMessageWrapper>
			<CloseButtonWrapper>
				<Button
					aria-label={formatMessage(i18n.closeButtonLabel)}
					onClick={onClose}
					testId="guest-banner-close-button"
					appearance="subtle"
					iconBefore={
						<EditorCloseIcon
							label={formatMessage(i18n.closeButtonLabel)}
							color={token('color.icon', N500)}
						/>
					}
				/>
			</CloseButtonWrapper>
			<SectionMessage
				appearance="discovery"
				icon={FeedbackIcon}
				testId={GUEST_BANNER_TEST_ID}
				title={formatMessage(i18n.bannerTitle)}
				actions={[
					<SectionMessageAction
						key="guest-section-link"
						testId="guest-section-link"
						onClick={onGuestsSectionLinkClick}
					>
						<FormattedMessage {...i18n.guestsSectionCTA} />
					</SectionMessageAction>,
					<SectionMessageAction
						key="guest-doc-link"
						testId="guest-doc-link"
						onClick={onGuestDocLinkClick}
					>
						<FormattedMessage {...i18n.guestsDocCTA} />
					</SectionMessageAction>,
				]}
			>
				<FormattedMessage {...i18n.bannerDescription} tagName="p" />
			</SectionMessage>
		</SectionMessageWrapper>
	);
};
